import type { VoteValue } from '../types/feedback';

interface PlayerFeedbackVote {
  userId: string;
  playerId: string;
  value: VoteValue;
  timestamp: number;
  playerName?: string;
}

const STORAGE_KEY = 'fantasy_fiesta_player_feedback';

export class PlayerFeedbackStorage {
  /**
   * Store a vote in localStorage
   */
  static storeVote(userId: string, playerId: string, value: VoteValue, playerName?: string): void {
    try {
      const existingVotes = this.getAllVotes();

      // Remove any existing vote from this user for this player
      const filteredVotes = existingVotes.filter(
        vote => !(vote.userId === userId && vote.playerId === playerId)
      );

      // Add new vote
      const newVote: PlayerFeedbackVote = {
        userId,
        playerId,
        value,
        timestamp: Date.now(),
        playerName
      };

      filteredVotes.push(newVote);

      localStorage.setItem(STORAGE_KEY, JSON.stringify(filteredVotes));

      console.log(`Player Feedback: User ${userId} voted ${value} for ${playerName || playerId}`);
    } catch (error) {
      console.error('Failed to store player feedback vote:', error);
    }
  }

  /**
   * Get all votes from localStorage
   */
  static getAllVotes(): PlayerFeedbackVote[] {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Failed to load player feedback votes:', error);
      return [];
    }
  }

  /**
   * Get votes for a specific player
   */
  static getPlayerVotes(playerId: string): PlayerFeedbackVote[] {
    return this.getAllVotes().filter(vote => vote.playerId === playerId);
  }

  /**
   * Get user's vote for a specific player
   */
  static getUserVote(userId: string, playerId: string): PlayerFeedbackVote | null {
    const votes = this.getAllVotes();
    return votes.find(vote => vote.userId === userId && vote.playerId === playerId) || null;
  }

  /**
   * Get vote statistics for a player
   */
  static getPlayerStats(playerId: string) {
    const playerVotes = this.getPlayerVotes(playerId);

    const stats = {
      playerId,
      totalVotes: playerVotes.length,
      tooHighCount: playerVotes.filter(v => v.value === -1).length,
      aboutRightCount: playerVotes.filter(v => v.value === 0).length,
      tooLowCount: playerVotes.filter(v => v.value === 1).length,
      averageVote: 0
    };

    if (stats.totalVotes > 0) {
      stats.averageVote = playerVotes.reduce((sum, vote) => sum + vote.value, 0) / stats.totalVotes;
    }

    return stats;
  }

  /**
   * Get feedback data formatted for the ranking algorithm
   */
  static getFeedbackForRankings(): Record<string, Array<{ userId: string; value: VoteValue; trust?: number; timestamp?: number }>> {
    const allVotes = this.getAllVotes();
    const feedbackData: Record<string, Array<{ userId: string; value: VoteValue; trust?: number; timestamp?: number }>> = {};

    allVotes.forEach(vote => {
      if (!feedbackData[vote.playerId]) {
        feedbackData[vote.playerId] = [];
      }

      feedbackData[vote.playerId].push({
        userId: vote.userId,
        value: vote.value,
        trust: 0.7, // Default trust for localStorage users
        timestamp: vote.timestamp
      });
    });

    return feedbackData;
  }

  /**
   * Clear all feedback data (for testing/reset)
   */
  static clearAllVotes(): void {
    localStorage.removeItem(STORAGE_KEY);
  }

  /**
   * Get total vote count across all players
   */
  static getTotalVoteCount(): number {
    return this.getAllVotes().length;
  }
}